import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import '@/App.css';
import Login from './pages/Login';
import Layout from './components/Layout';
import OfflineIndicator from './components/OfflineIndicator';
import { Toaster } from './components/ui/sonner';

// Lazy-loaded pages → each page becomes a separate JS chunk loaded on demand.
// Cuts initial bundle size dramatically (heavy deps like html2pdf, recharts,
// qrcode.react only load when their page is visited).
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Sales = lazy(() => import('./pages/Sales'));
const Purchases = lazy(() => import('./pages/Purchases'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Customers = lazy(() => import('./pages/Customers'));
const Repairs = lazy(() => import('./pages/Repairs'));
const Installments = lazy(() => import('./pages/Installments'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const Debts = lazy(() => import('./pages/Debts'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const CashBox = lazy(() => import('./pages/CashBox'));
const Accessories = lazy(() => import('./pages/Accessories'));
const Android = lazy(() => import('./pages/Android'));
const Notes = lazy(() => import('./pages/Notes'));

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-zinc-400 text-sm">
    جاري التحميل...
  </div>
);

// Maps the route key to a human label (for the "no access" page)
const PAGE_LABELS = {
  dashboard: 'لوحة التحكم',
  sales: 'المبيعات',
  purchases: 'المشتريات',
  inventory: 'المخزن',
  customers: 'العملاء',
  repairs: 'الصيانة',
  installments: 'التقسيط',
  debts: 'الذمم',
  cashbox: 'الصندوق',
  accessories: 'الإكسسوارات',
  android: 'الأندرويد',
  reports: 'التقارير',
  settings: 'الإعدادات',
};

// Ordered list used to pick the first allowed page for employees
const PERM_ORDER = ['sales', 'purchases', 'inventory', 'customers', 'repairs', 'installments', 'debts', 'cashbox', 'accessories', 'android', 'reports'];
const PERM_TO_PATH = {
  sales: '/sales', purchases: '/purchases', inventory: '/inventory', customers: '/customers',
  repairs: '/repairs', installments: '/installments', debts: '/debts', cashbox: '/cash-box',
  accessories: '/accessories', android: '/android', reports: '/reports',
};

const firstAllowedPath = (user) => {
  const perms = user?.permissions?.pages || {};
  for (const k of PERM_ORDER) if (perms[k] === true) return PERM_TO_PATH[k];
  return null; // no allowed page
};

function NoAccess({ pageKey }) {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="no-access-page">
      <div className="text-5xl mb-4">🔒</div>
      <h2 className="text-xl font-bold text-white mb-2">ليس لديك صلاحية لعرض هذا القسم</h2>
      <p className="text-sm text-zinc-400">قسم <span className="text-[#007aff] font-bold">{PAGE_LABELS[pageKey] || pageKey}</span> متاح فقط للمدير أو حسب صلاحيات الموظف.</p>
      <p className="text-xs text-zinc-500 mt-2">يرجى التواصل مع المدير لمنحك الصلاحية.</p>
    </div>
  );
}

// Guards a route by required page permission key. Admin always passes.
function Protected({ user, permKey, adminOnly = false, children }) {
  const isAdmin = user?.role === 'admin';
  if (adminOnly && !isAdmin) return <NoAccess pageKey={permKey} />;
  const perms = user?.permissions?.pages || {};
  if (!isAdmin && perms[permKey] !== true) return <NoAccess pageKey={permKey} />;
  return children;
}

// Decides what to render at root path "/":
//   - Admin → Dashboard
//   - Employee → redirect to first allowed page; if none → NoAccess
function HomeRoute({ user, DashboardCmp }) {
  if (user?.role === 'admin') return <DashboardCmp />;
  const target = firstAllowedPath(user);
  if (target) return <Navigate to={target} replace />;
  return <NoAccess pageKey="dashboard" />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ====== One-time cache reset if backend URL has changed ======
    // This protects against stale service-worker caches pointing to old backend
    // URLs (a common issue in preview/staging environments).
    const currentBackend = process.env.REACT_APP_BACKEND_URL || '';
    const savedBackend = localStorage.getItem('last_backend_url');
    if (savedBackend && savedBackend !== currentBackend) {
      // Backend URL changed → wipe everything to force a clean state
      (async () => {
        try {
          localStorage.clear();
          sessionStorage.clear();
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
        } catch (e) { /* ignore */ }
        localStorage.setItem('last_backend_url', currentBackend);
        window.location.reload();
      })();
      return;
    }
    localStorage.setItem('last_backend_url', currentBackend);

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      // Refresh user info (incl. permissions) silently from server
      fetch(`${currentBackend}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.ok ? r.json() : null)
        .then((fresh) => {
          if (fresh && fresh.id) {
            const merged = { ...parsed, ...fresh };
            localStorage.setItem('user', JSON.stringify(merged));
            setUser(merged);
          }
        })
        .catch(() => {});
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-white">جاري التحميل...</div>
    </div>;
  }

  // Tauri uses tauri:// protocol → use HashRouter. Web uses BrowserRouter.
  const isTauri = typeof window !== 'undefined' && (
    window.__TAURI_IPC__ !== undefined ||
    window.__TAURI__ !== undefined ||
    (window.location && window.location.protocol === 'tauri:')
  );
  const Router = isTauri ? HashRouter : BrowserRouter;

  return (
    <div className="App" dir="rtl">
      <OfflineIndicator />
      <Router>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
            <Route path="/" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
              <Route index element={<HomeRoute user={user} DashboardCmp={Dashboard} />} />
              <Route path="sales" element={<Protected user={user} permKey="sales"><Sales /></Protected>} />
              <Route path="purchases" element={<Protected user={user} permKey="purchases"><Purchases /></Protected>} />
              <Route path="inventory" element={<Protected user={user} permKey="inventory"><Inventory /></Protected>} />
              <Route path="customers" element={<Protected user={user} permKey="customers"><Customers /></Protected>} />
              <Route path="repairs" element={<Protected user={user} permKey="repairs"><Repairs /></Protected>} />
              <Route path="installments" element={<Protected user={user} permKey="installments"><Installments /></Protected>} />
              <Route path="reports" element={<Protected user={user} permKey="reports"><Reports /></Protected>} />
              <Route path="debts" element={<Protected user={user} permKey="debts"><Debts /></Protected>} />
              <Route path="suppliers" element={<Protected user={user} permKey="debts"><Suppliers /></Protected>} />
              <Route path="cash-box" element={<Protected user={user} permKey="cashbox"><CashBox /></Protected>} />
              <Route path="accessories" element={<Protected user={user} permKey="accessories"><Accessories /></Protected>} />
              <Route path="android" element={<Protected user={user} permKey="android"><Android /></Protected>} />
              <Route path="notes" element={<Notes />} />
              <Route path="settings" element={<Protected user={user} permKey="settings" adminOnly><Settings /></Protected>} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
      <Toaster position="top-center" dir="rtl" />
    </div>
  );
}

export default App;
